import { MutationOptions, QueryOptions, unwrap } from './common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export interface EinmalAuftrag {
  id: string | number;
  name: string;
  description: string;
  duration: string;
  priority: number;
  deleted: boolean;
  created_at: string;
}

export interface RunningEinmalAuftrag {
  id: string | number;
  einmal_auftrag: EinmalAuftrag;
  employee: number;
  status: 'R' | 'P';
  start: string;
  duration: string;
}

export function useEinmalAuftraege<T = EinmalAuftrag[]>(
  options?: QueryOptions<EinmalAuftrag[], T>,
) {
  return useQuery({
    ...options,
    queryKey: ['einmal_auftrag', 'list'],
    queryFn: () =>
      axios.get<EinmalAuftrag[]>('/einmal_auftraege/available/').then(unwrap),
  });
}

export function useRunningEinmalAuftraege<T = RunningEinmalAuftrag[]>(
  employeeId: number,
  options?: QueryOptions<RunningEinmalAuftrag[], T>,
) {
  return useQuery({
    ...options,
    queryKey: ['running_einmal_auftrag', employeeId],
    queryFn: () =>
      axios
        .get<RunningEinmalAuftrag[]>(`/running_einmal_auftrag/${employeeId}/`)
        .then(unwrap),
  });
}

export function useStartEinmalAuftrag(
  options?: MutationOptions<RunningEinmalAuftrag, { einmalAuftragId: number }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ einmalAuftragId }) =>
      axios
        .post<RunningEinmalAuftrag>(`/start_einmal_auftrag/${einmalAuftragId}/`)
        .then(unwrap),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['running_einmal_auftrag'],
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function usePauseEinmalAuftrag(
  options?: MutationOptions<RunningEinmalAuftrag, { runningEinmalAuftragId: number }>,
) {
  return useMutation({
    ...options,
    mutationFn: ({ runningEinmalAuftragId }) =>
      axios
        .patch<RunningEinmalAuftrag>(`/pause_einmal_auftrag/${runningEinmalAuftragId}/`)
        .then(unwrap),
    onSuccess: (...args) => {
      options?.onSuccess?.(...args);
    },
  });
}

export function useResumeEinmalAuftrag(
  options?: MutationOptions<RunningEinmalAuftrag, { runningEinmalAuftragId: number }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ runningEinmalAuftragId }) =>
      axios
        .patch<RunningEinmalAuftrag>(`/start_paused_einmal_auftrag/${runningEinmalAuftragId}/`)
        .then(unwrap),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['running_einmal_auftrag'],
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useEndEinmalAuftrag(
  options?: MutationOptions<void, { runningEinmalAuftragId: number }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ runningEinmalAuftragId }) =>
      axios
        .post<void>(`/end_einmal_auftrag/${runningEinmalAuftragId}/`)
        .then(unwrap),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['running_einmal_auftrag'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['einmal_auftrag'],
      });
      options?.onSuccess?.(...args);
    },
  });
}
